import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import type { QuoteBlock, QuoteBlockPayload } from '@/types/domain'

/* The not-included items and disclaimers a quote prints (Story 5.2, FR-60).
 *
 * Beside useRouteOptions.ts rather than inside useQuote.ts, mirroring the
 * backend's block_*.go seam: these serve one story and one endpoint family, and
 * the file split is the same one on both sides.
 *
 * Their OWN query rather than a field on the detail. The endpoint is separate
 * because the two lists are edited on one screen and printed on one document,
 * and taxing every quote read with them would fetch a list most visits never
 * open. The key sits UNDER the detail's, so a send — which freezes these into
 * the revision — refreshes them with everything else it invalidates.
 *
 * Nothing here is optimistic. A block is a couple of sentences somebody types
 * once, not a cost line entered fifty times, and two of the writes below can be
 * REFUSED by the server (the quote has moved past draft, the kind is full) —
 * rendering them as done first would show exactly the state the refusal exists
 * to prevent.
 */

function useInvalidateBlocks(quoteId: string) {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.blocks(quoteId) })
  }
}

export function useQuoteBlocks(quoteId: string) {
  return useQuery<QuoteBlock[]>({
    queryKey: queryKeys.quotes.blocks(quoteId),
    queryFn: async () =>
      (await api.get<{ items: QuoteBlock[] }>(`/quotes/${quoteId}/blocks`)).items,
  })
}

export function useCreateQuoteBlock(quoteId: string) {
  const invalidate = useInvalidateBlocks(quoteId)
  return useMutation<QuoteBlock, Error, QuoteBlockPayload>({
    mutationFn: (payload) => api.post<QuoteBlock>(`/quotes/${quoteId}/blocks`, payload),
    onSuccess: invalidate,
  })
}

export function useUpdateQuoteBlock(quoteId: string) {
  const invalidate = useInvalidateBlocks(quoteId)
  return useMutation<QuoteBlock, Error, { blockId: string; payload: QuoteBlockPayload }>({
    /* A whole-record PUT, like the route option's — except for the KIND, which
       the server refuses to change: moving a disclaimer into the exclusions is
       a different item, deleted and re-added, and a PUT that carried it would
       let a priced exclusion become a priced disclaimer in one write. */
    mutationFn: ({ blockId, payload }) =>
      api.put<QuoteBlock>(`/quotes/${quoteId}/blocks/${blockId}`, payload),
    onSuccess: invalidate,
  })
}

export function useDeleteQuoteBlock(quoteId: string) {
  const invalidate = useInvalidateBlocks(quoteId)
  return useMutation<void, Error, string>({
    mutationFn: (blockId) => api.delete<void>(`/quotes/${quoteId}/blocks/${blockId}`),
    onSuccess: invalidate,
  })
}

/** The quote already carries as many blocks of that kind as it may (FR-60).
 *
 *  A predicate here rather than a string comparison in the component, like the
 *  FR-3 refusals in useRouteOptions: the wire token is spelled in exactly one
 *  place per side. */
export function blocksCapped(error: unknown): boolean {
  return (
    error instanceof ApiError && error.problem.detail === 'document-blocks-capped'
  )
}
